import { memo, useCallback } from 'react';
import { withNamespaces } from 'react-i18next';
import { Card, CardBody, CardTitle, Table } from 'reactstrap';

import CrossCheckCell from './components/CrossCheckCell';
import useSupplierOverviewCrossCheckAnalysis from './hooks/useSupplierOverviewCrossCheckAnalysis';

const CrossCheckAnalysisSkeleton = () => (
    <Card className="overview-cross-check-analysis mb-4">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '25%' }} />
            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '220px' }} />
        </CardBody>
    </Card>
);

const CrossCheckAnalysis = ({
    t,
    supplierId,
}) => {
    const fetchCrossCheckAnalysisQuery = useSupplierOverviewCrossCheckAnalysis({
        supplierId,
        t,
    });

    const {
        reportSources = [],
        criteria = [],
    } = fetchCrossCheckAnalysisQuery.data || {};

    const noDataIndication = useCallback(() => (
        <div className="text-center py-4 text-muted">
            {t('No cross-check analysis data available.')}
        </div>
    ), [t]);

    if (fetchCrossCheckAnalysisQuery.isLoading || fetchCrossCheckAnalysisQuery.isFetching) {
        return <CrossCheckAnalysisSkeleton />;
    }

    const hasData = reportSources.length > 0 && criteria.length > 0;

    return (
        <Card className="overview-cross-check-analysis mb-4">
            <CardBody>
                <CardTitle tag="h6" className="overview-section-title mb-4">
                    {t('Cross-Check Analysis')}
                </CardTitle>

                <div className="table-responsive overview-cross-check-analysis__table-wrapper">
                    {!hasData ? (
                        noDataIndication()
                    ) : (
                        <Table className="overview-cross-check-analysis__table mb-0" responsive>
                            <thead>
                                <tr>
                                    <th className="overview-cross-check-analysis__criterion-header">
                                        KYB/Due Diligence Report
                                    </th>
                                    {reportSources.map((source) => (
                                        <th
                                            key={source.key}
                                            className="overview-cross-check-analysis__source-header"
                                        >
                                            {source.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {criteria.map((criterion) => (
                                    <tr key={criterion.key}>
                                        <td className="overview-cross-check-analysis__criterion-cell">
                                            {criterion.label}
                                        </td>
                                        {reportSources.map((source) => (
                                            <td
                                                key={`${criterion.key}-${source.key}`}
                                                className="overview-cross-check-analysis__value-cell"
                                            >
                                                <CrossCheckCell value={criterion.values?.[source.key]} />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(memo(CrossCheckAnalysis));
